import { authenticatedFetch, getAuthHeaders } from './authApi';
import { getEnv } from '../utils/env';
import type { MatchTypeMetadata } from '../types';

type UnknownRecord = Record<string, unknown>;

const toApiBaseUrl = (): string => getEnv('VITE_AI_API_URL', '').replace(/\/$/, '');

const parseJson = async (response: Response): Promise<unknown> => {
  try {
    return await response.json();
  } catch {
    return null;
  }
};

const extractData = (payload: unknown): unknown => {
  if (payload && typeof payload === 'object' && 'data' in (payload as UnknownRecord)) {
    return (payload as UnknownRecord).data;
  }
  return payload;
};

const normalizeMetaNumeric = (value: unknown): number | null => {
  if (value === null || value === undefined) return null;
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return null;
  return numeric;
};

const extractMessage = (payload: unknown, fallback: string): string => {
  if (payload && typeof payload === 'object' && 'error' in (payload as UnknownRecord)) {
    const errorValue = (payload as UnknownRecord).error;
    if (typeof errorValue === 'string' && errorValue.trim().length > 0) return errorValue;
    if (errorValue && typeof errorValue === 'object' && 'message' in (errorValue as UnknownRecord)) {
      const message = (errorValue as UnknownRecord).message;
      if (typeof message === 'string' && message.trim().length > 0) return message;
    }
  }
  if (payload && typeof payload === 'object' && 'message' in (payload as UnknownRecord)) {
    const message = (payload as UnknownRecord).message;
    if (typeof message === 'string' && message.trim().length > 0) return message;
  }
  return fallback;
};

const matchTypesRequest = async (path: string, options: RequestInit = {}): Promise<unknown> => {
  const baseUrl = toApiBaseUrl();
  if (!baseUrl) {
    throw new Error('VITE_AI_API_URL is not configured');
  }

  const response = await authenticatedFetch(`${baseUrl}${path}`, {
    ...options,
    credentials: 'include',
    headers: getAuthHeaders(options.headers || {}),
  });

  const payload = await parseJson(response);
  if (!response.ok) {
    throw new Error(extractMessage(payload, `Match types API request failed (${response.status})`));
  }

  return extractData(payload);
};

export const listMatchTypesApi = async (): Promise<unknown[]> => {
  const payload = await matchTypesRequest('/api/v1/match-types', { method: 'GET' });
  return Array.isArray(payload) ? payload : [];
};

export const getMatchTypeMetaApi = async (matchTypeKey: string): Promise<MatchTypeMetadata | null> => {
  const baseUrl = toApiBaseUrl();
  if (!baseUrl) {
    throw new Error('VITE_AI_API_URL is not configured');
  }

  const response = await authenticatedFetch(`${baseUrl}/api/v1/match-types/${encodeURIComponent(matchTypeKey)}`, {
    method: 'GET',
    credentials: 'include',
    headers: getAuthHeaders(),
  });

  if (response.status === 404) {
    return null;
  }

  const payload = await parseJson(response);
  if (!response.ok) {
    throw new Error(`Failed to fetch match type: ${response.status}`);
  }

  const data = extractData(payload);
  if (!data || typeof data !== 'object') {
    return null;
  }

  const row = data as UnknownRecord;
  return {
    total_playing_time: normalizeMetaNumeric(row.total_playing_time),
    quarters: normalizeMetaNumeric(row.quarters),
    quarter_time: normalizeMetaNumeric(row.quarter_time),
    field: typeof row.field === 'string' ? row.field : null,
  };
};
